<script setup lang="ts">
import { computed } from 'vue'
import { useCtaStore } from '@/stores/cta/store'
import Plan, { type PlanData } from '@/views/settings/Plan.vue'
import ErrorBox from '@/components/ErrorBox.vue'
import Spinner from '@/components/Base/Spinner.vue'

const props = withDefaults(
  defineProps<{
    page?: string
    upgradeOnly?: boolean
    selectedPlanData?: PlanData
    buttonDisabled?: boolean
    isSaving?: boolean
    ctaResetNecessary?: boolean
  }>(),
  {
    page: '',
    selectedPlanData: () => ({
      name: '',
      description: '',
    }),
  }
)

const emit = defineEmits<{
  (e: 'backToSelectPlanPage', page: string): void
  (e: 'buyPlan', selectedPlanData: PlanData): void
}>()

const ctaStore = useCtaStore()

const maxPlanCode = 'CPX00250'

const canChangePlan = computed(() => {
  if (props.upgradeOnly) {
    return ctaStore.get.current !== maxPlanCode
  }

  return (
    ctaStore.get.current !== maxPlanCode ||
    props.selectedPlanData.shortcode !== maxPlanCode
  )
})

function buyPlan() {
  emit('buyPlan', props.selectedPlanData)
}

function backToSelectPlanPage() {
  emit('backToSelectPlanPage', props.page)
}
</script>

<template>
  <div>
    <div v-if="upgradeOnly && canChangePlan" class="mb-8">
      <div class="mb-4 text-sm text-neutral-700" data-test="plan-upgrade-text">
        {{ $t('global.common.upgradePlan.upgrade') }}
      </div>

      <h5 class="eb-heading text-md">
        {{ $t('settings.plan.modal.newPlan') }}
      </h5>

      <Plan :plan-data="selectedPlanData" />
    </div>

    <!-- Comes from overview page and plans are not available to upgrade -->
    <div
      v-if="upgradeOnly && !canChangePlan"
      class="mb-8 text-sm text-neutral-700"
    >
      {{ $t('global.common.upgradePlan.upgradeNotPossible') }}
    </div>

    <!-- Comes from settings page and can upgrade plan -->
    <div
      v-if="!upgradeOnly && canChangePlan"
      class="mb-8"
      :class="isSaving ? 'opacity-25' : ''"
    >
      <div class="mb-4 text-sm text-neutral-700">
        {{ $t('settings.plan.modal.chosenFollowingPlan') }}
      </div>

      <Plan class="mb-4" :plan-data="selectedPlanData" />

      <div
        class="text-sm text-neutral-700"
        :class="ctaResetNecessary ? 'mb-8' : ''"
      >
        {{ $t('settings.plan.modal.chosenPlanInformation') }}
      </div>
      <ErrorBox
        v-if="ctaResetNecessary"
        :message="$t('settings.plan.modal.downgrade.reset')"
      />
    </div>
    <div v-if="isSaving" class="absolute block right-1/2 top-1/2">
      <Spinner class="mt-[-24px] mr-[-24px]" />
    </div>
    <div class="flex justify-end gap-4">
      <button
        class="inline-flex eb-legacy-button eb-legacy-button--md eb-legacy-button--secondary"
        @click="backToSelectPlanPage"
        :disabled="isSaving"
      >
        {{ $t('global.action.back') }}
      </button>

      <button
        class="inline-flex eb-legacy-button eb-legacy-button--md eb-legacy-button--primary"
        :disabled="!canChangePlan || buttonDisabled || isSaving"
        data-test="modal-plan-buynow-button"
        @click="buyPlan"
      >
        {{
          $t(
            ctaResetNecessary
              ? 'global.action.confirm'
              : 'settings.plan.modal.buyNow'
          )
        }}
      </button>
    </div>
  </div>
</template>
